import React, { useState } from 'react';
import { Box, Checkbox, HStack, Heading, Icon, Text, VStack } from '@chakra-ui/react';
import { MessageCircle } from 'lucide-react';
import { useGetClarityProfileOptInQuery, useUpdateClarityProfileOptInMutation } from './api';
import { GENERIC_CLARITY_ERROR, describeClarityError } from './types';
import { useClarityAccess } from './use-clarity-access';

interface ClarityChatbotOptInSectionProps {
  profileId: number;
  isOpen: boolean;
}

/** SuperGod-only "Clarity chatbot" opt-in for a doctor's profile, read and written through `/admin/api/profiles/{id}`. */
export function ClarityChatbotOptInSection({ profileId, isOpen }: Readonly<ClarityChatbotOptInSectionProps>) {
  const access = useClarityAccess();
  const { currentData, isError, isFetching } = useGetClarityProfileOptInQuery(profileId, { skip: !access || !isOpen });
  const [save, saveState] = useUpdateClarityProfileOptInMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // The hidden input is uncontrolled (defaultChecked); remount it after a failed save so the
  // DOM state matches the kept cached value again.
  const [revision, setRevision] = useState(0);

  if (!access || !isOpen) return null;

  const onCheckedChange = async (checked: boolean) => {
    setErrorMessage(null);
    try {
      await save({ profileId, enabled: checked }).unwrap();
    } catch (error) {
      const description = describeClarityError(error);
      setErrorMessage(description.fields ? Object.values(description.fields).join(' ') : description.message ?? GENERIC_CLARITY_ERROR);
      setRevision((value) => value + 1);
    }
  };

  const renderBody = () => {
    if (!currentData) {
      if (isError && !isFetching) return <Text fontSize="sm" color="red.500" role="alert">{GENERIC_CLARITY_ERROR}</Text>;
      return <Text fontSize="sm" color="gray.500">Loading…</Text>;
    }
    if (currentData.enabled === null) {
      return <Text fontSize="sm" color="gray.500">Not available: the API did not return the chatbot opt-in</Text>;
    }
    return (
      <VStack align="start" gap={1}>
        <Checkbox.Root
          key={revision}
          checked={currentData.enabled}
          disabled={saveState.isLoading}
          onCheckedChange={(details) => onCheckedChange(details.checked === true)}
          colorPalette="brand"
        >
          <Checkbox.HiddenInput />
          {/* The default unchecked border (border.emphasized = gray.700 in dark) matches the panel's dark background. */}
          <Checkbox.Control borderColor={{ base: 'gray.400', _dark: 'gray.400' }} />
          <Checkbox.Label>Show the patient chatbot on this doctor's public profile</Checkbox.Label>
        </Checkbox.Root>
        <Text fontSize="xs" color="gray.500">
          Takes effect only once the doctor's organization is connected to Clarity and their resources are ready.
        </Text>
      </VStack>
    );
  };

  return (
    <Box
      p={4}
      borderWidth={1}
      borderRadius="md"
      borderColor="gray.200"
      bg="white"
      _dark={{ borderColor: 'gray.600', bg: 'gray.700' }}
      data-testid="clarity-chatbot-opt-in"
    >
      <VStack align="start" gap={3}>
        <HStack>
          <Icon as={MessageCircle} boxSize={4} color="brand.500" />
          <Heading size="sm" color="brand.600" _dark={{ color: 'brand.300' }}>Clarity chatbot</Heading>
        </HStack>
        {renderBody()}
        {errorMessage && <Text fontSize="sm" color="red.500" role="alert">{errorMessage}</Text>}
      </VStack>
    </Box>
  );
}
